import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getArticles } from "@/lib/api";
import type { BlogArticle } from "@/lib/types";

const categoryColors: Record<string, string> = {
  Consejos: "bg-[#00b4c8]/10 text-[#00b4c8]",
  Recetas: "bg-[#fde68a]/60 text-[#b08000]",
  "Causas Sociales": "bg-[#f9b8c8]/50 text-[#d4607a]",
  Noticias: "bg-[#c9b8f0]/40 text-[#7c5cbf]",
  Tendencias: "bg-[#f9b8c8]/40 text-[#d4607a]",
};

function ArticleDetailSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-80 md:h-[480px] bg-[#f9d0dc] w-full"></div>
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 space-y-5">
        <div className="h-4 bg-[#f9d0dc] rounded-full w-24"></div>
        <div className="h-10 bg-[#f9d0dc] rounded-full w-3/4"></div>
        <div className="h-10 bg-[#f9d0dc] rounded-full w-1/2"></div>
        <div className="flex gap-4 mt-4">
          <div className="h-3 bg-[#f9d0dc] rounded-full w-28"></div>
          <div className="h-3 bg-[#f9d0dc] rounded-full w-20"></div>
          <div className="h-3 bg-[#f9d0dc] rounded-full w-16"></div>
        </div>
        <div className="pt-6 space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className={`h-4 bg-[#f9d0dc] rounded-full ${i % 3 === 2 ? "w-2/3" : "w-full"}`}></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RelatedCard({ article }: { article: BlogArticle }) {
  return (
    <Link
      to={`/blog/${article.id}`}
      className="group flex gap-4 items-start hover:bg-[#fff5f8] rounded-xl p-3 transition-colors"
    >
      <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${categoryColors[article.category] ?? "bg-gray-100 text-gray-600"}`}>
          {article.category}
        </span>
        <h4 className="text-sm font-bold text-[#2d1a0e] mt-1.5 leading-tight line-clamp-2 group-hover:text-[#d4607a] transition-colors">
          {article.title}
        </h4>
        <p className="text-xs text-[#b09080] mt-1 flex items-center gap-1">
          <i className="ri-time-line"></i> {article.read_time}
        </p>
      </div>
    </Link>
  );
}

export default function BlogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [allArticles, setAllArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setNotFound(false);

      try {
        const data = await getArticles();

        setAllArticles(data);

        const selectedArticle = data.find(
          (article: BlogArticle) => article.id === Number(id)
        );

        if (!selectedArticle) {
          setNotFound(true);
        } else {
          setArticle(selectedArticle);
        }
      } catch (error) {
        console.error("Error al cargar artículos:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const currentIndex = allArticles.findIndex((a) => a.id === Number(id));
  const prevArticle = currentIndex > 0 ? allArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null;
  const related = allArticles
    .filter((a) => a.id !== Number(id) && a.category === article?.category)
    .slice(0, 3);
  const moreArticles = allArticles
    .filter((a) => a.id !== Number(id) && !related.find((r) => r.id === a.id))
    .slice(0, 3 - related.length);
  const sidebarArticles = [...related, ...moreArticles].slice(0, 4);

  const paragraphs = article?.content
    ? article.content.split("\n").filter((p) => p.trim() !== "")
    : [];

  if (loading) return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <ArticleDetailSkeleton />
      <Footer />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <div className="flex-1 flex flex-col items-center justify-center py-24 px-4">
        <i className="ri-article-line text-6xl text-[#f9b8c8] mb-4"></i>
        <h2 className="text-2xl font-bold text-[#2d1a0e] mb-2">
          Artículo no encontrado
        </h2>
        <p className="text-[#6b5b6e] mb-6 text-sm">El artículo que buscas no existe o fue eliminado.</p>
        <Link
          to="/blog"
          className="flex items-center gap-2 bg-[#d4607a] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#b84d65] transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> Volver al Blog
        </Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Image */}
      <div className="relative h-80 md:h-[480px] w-full overflow-hidden">
        <img
          src={article!.image}
          alt={article!.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

        {/* Breadcrumb */}
        <div className="absolute top-28 left-0 right-0 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-white/70">
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white/90 line-clamp-1">{article!.title}</span>
            </nav>
          </div>
        </div>

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 px-4 md:px-8 pb-10">
          <div className="max-w-7xl mx-auto">
            <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-3 ${categoryColors[article!.category] ?? "bg-white/20 text-white"}`}>
              {article!.category}
            </span>
            <h1
              className="text-3xl md:text-5xl font-bold text-white leading-tight max-w-3xl"
            >
              {article!.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* Main content */}
          <article className="flex-1 min-w-0">

            {/* Author + meta */}
            <div className="flex flex-wrap items-center gap-5 pb-6 mb-8 border-b border-[#f5e6d3]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f9b8c8] text-[#d4607a] font-bold text-sm flex-shrink-0">
                  {article!.author.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#2d1a0e]">{article!.author}</p>
                  <p className="text-xs text-[#b09080]">Autor</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-[#b09080]">
                <i className="ri-calendar-line"></i>
                <span>{article!.date}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-[#b09080]">
                <i className="ri-time-line"></i>
                <span>{article!.read_time} de lectura</span>
              </div>
              {article!.featured && (
                <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                  <i className="ri-star-fill"></i> Destacado
                </span>
              )}
            </div>

            {/* Excerpt */}
            <p
              className="text-lg text-[#5a3e2b] leading-relaxed mb-8 font-medium border-l-4 border-[#d4607a] pl-5 italic"
            >
              {article!.excerpt}
            </p>

            {/* Body content */}
            <div className="prose-custom space-y-5">
              {paragraphs.length > 0 ? (
                paragraphs.map((para, i) => (
                  <p key={i} className="text-[#4a3728] text-base leading-relaxed">
                    {para}
                  </p>
                ))
              ) : (
                <p className="text-[#4a3728] text-base leading-relaxed">{article!.content}</p>
              )}
            </div>

            {/* Share */}
            <div className="mt-12 pt-8 border-t border-[#f5e6d3]">
              <p className="text-sm font-semibold text-[#2d1a0e] mb-3">Compartir artículo</p>
              <div className="flex gap-3">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(article!.title + " - " + window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366]/10 text-[#25D366] text-sm font-semibold hover:bg-[#25D366]/20 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i> WhatsApp
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#f5f0ff] text-[#7c5cbf] text-sm font-semibold hover:bg-[#e8e0f8] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-link"></i> Copiar link
                </button>
              </div>
            </div>

            {/* Prev / Next navigation */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {prevArticle ? (
                <Link
                  to={`/blog/${prevArticle.id}`}
                  className="group flex items-center gap-4 p-4 rounded-2xl bg-[#fff5f8] hover:bg-[#f9e8ee] transition-colors"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f9b8c8] text-[#d4607a] flex-shrink-0 group-hover:bg-[#d4607a] group-hover:text-white transition-colors">
                    <i className="ri-arrow-left-line text-sm"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-[#b09080] mb-0.5">Artículo anterior</p>
                    <p className="text-sm font-semibold text-[#2d1a0e] line-clamp-1 group-hover:text-[#d4607a] transition-colors">
                      {prevArticle.title}
                    </p>
                  </div>
                </Link>
              ) : <div />}

              {nextArticle ? (
                <Link
                  to={`/blog/${nextArticle.id}`}
                  className="group flex items-center gap-4 p-4 rounded-2xl bg-[#fff5f8] hover:bg-[#f9e8ee] transition-colors sm:flex-row-reverse text-right"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f9b8c8] text-[#d4607a] flex-shrink-0 group-hover:bg-[#d4607a] group-hover:text-white transition-colors">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-[#b09080] mb-0.5">Siguiente artículo</p>
                    <p className="text-sm font-semibold text-[#2d1a0e] line-clamp-1 group-hover:text-[#d4607a] transition-colors">
                      {nextArticle.title}
                    </p>
                  </div>
                </Link>
              ) : <div />}
            </div>

            {/* Back to blog */}
            <div className="mt-8 text-center">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#d4607a] hover:text-[#b84d65] transition-colors"
              >
                <i className="ri-arrow-left-line"></i> Ver todos los artículos
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="w-full lg:w-80 flex-shrink-0 space-y-8">

            {/* More articles */}
            {sidebarArticles.length > 0 && (
              <div className="bg-[#fff5f8] rounded-2xl p-5">
                <h3
                  className="text-base font-bold text-[#2d1a0e] mb-4"
                >
                  {related.length > 0 ? "Artículos relacionados" : "Más artículos"}
                </h3>
                <div className="space-y-1">
                  {sidebarArticles.map((a) => (
                    <RelatedCard key={a.id} article={a} />
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-br from-[#d4607a] to-[#b84d65] rounded-2xl p-6 text-white">
              <h3
                className="text-lg font-bold mb-2"
              >
                ¿Antojo de algo dulce?
              </h3>
              <p className="text-white/80 text-sm mb-4 leading-relaxed">
                Explora nuestro menú completo de pasteles, galletas y más delicias artesanales.
              </p>
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 bg-white text-[#d4607a] text-sm font-bold px-5 py-2.5 rounded-full hover:bg-[#fff5f8] transition-colors whitespace-nowrap"
              >
                Ver Menú <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {/* Categories */}
            <div className="bg-white border border-[#f5e6d3] rounded-2xl p-5">
              <h3
                className="text-base font-bold text-[#2d1a0e] mb-4"
              >
                Categorías
              </h3>
              <div className="flex flex-wrap gap-2">
                {Array.from(new Set(allArticles.map((a) => a.category))).map((cat) => (
                  <Link
                    key={cat}
                    to="/blog"
                    onClick={() => navigate("/blog")}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors whitespace-nowrap ${categoryColors[cat] ?? "bg-gray-100 text-gray-600"}`}
                  >
                    {cat}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}
